import { Link } from 'react-router'
import { ArrowRight, CheckCircle } from 'lucide-react'

/**
 * GEO (Generative Engine Optimization) — Answer-first block.
 * Places the direct answer at the TOP of the page so AI systems
 * (ChatGPT, Perplexity, Google SGE) cite it immediately.
 *
 * Format: inverted pyramid — most important info first.
 * AI systems cite this format 60% more often.
 */

interface GeoAnswerProps {
  question: string
  answer: string
  details?: string[]
  priceRange?: { min: number; max: number; unit: string }
  ctaUrl?: string
  ctaLabel?: string
}

export function GeoAnswerBlock({ question, answer, details, priceRange, ctaUrl, ctaLabel }: GeoAnswerProps) {
  return (
    <div className="rounded-2xl border border-primary/10 bg-primary/[0.02] p-5 sm:p-6">
      {/* Question as H2 — matches what people search */}
      <h2 className="text-base font-bold text-slate-900 sm:text-lg">{question}</h2>

      {/* Direct answer — first 2 sentences are what AI cites */}
      <p className="mt-2 text-sm leading-relaxed text-slate-700">{answer}</p>

      {/* Key details as bullet list — scannable, AI-friendly */}
      {details && details.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {details.map((d, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Price range — highly cited by AI */}
      {priceRange && (
        <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-xs">
          💰 Prix moyen : {priceRange.min.toLocaleString('fr-FR')} — {priceRange.max.toLocaleString('fr-FR')} € / {priceRange.unit}
        </div>
      )}

      {/* CTA */}
      {ctaUrl && (
        <div className="mt-4">
          <Link to={ctaUrl} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
            {ctaLabel ?? 'Obtenir un devis gratuit'} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  )
}

/**
 * Semantic silo links — related pages within the same topic cluster.
 * Creates internal linking structure that signals topical authority.
 */
export function SiloLinks({ title, links }: {
  title: string
  links: Array<{ href: string; label: string; description?: string }>
}) {
  if (links.length === 0) return null

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</h3>
      <div className="mt-3 space-y-2">
        {links.map((link) => (
          <Link
            key={link.href}
            to={link.href}
            className="group flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors hover:bg-slate-50"
          >
            <div>
              <span className="font-medium text-slate-700 group-hover:text-primary">{link.label}</span>
              {link.description && <span className="ml-2 text-xs text-slate-400">{link.description}</span>}
            </div>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-300 group-hover:text-primary" />
          </Link>
        ))}
      </div>
    </div>
  )
}
